import React, { useState, useEffect, useMemo } from "react";
import AddProductForm from "./AddProductForm";
import InventoryTransferForm from "./InventoryTransferForm";
import PrintLabelForm from "./PrintLabelForm";
import ImportProductForm from "./ImportProductForm";
import PrintRetailLabelForm from "./PrintRetailLabelForm";
import EditProductForm from "./EditProductForm";
import { clearSessionAndRedirect } from "../services/session.js";

const InventorySearch = ({ refreshTrigger }) => {
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [showPrintLabelForm, setShowPrintLabelForm] = useState(false);
  const [showPrintRetailLabelForm, setShowPrintRetailLabelForm] = useState(false);
  const [refreshProducts, setRefreshProducts] = useState(false);
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const [showImportProductForm, setShowImportProductForm] = useState(false);
  const [showEditProductForm, setShowEditProductForm] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [secureData, setSecureData] = useState(null);
  const [error, setError] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [statusFilter, setStatusFilter] = useState("ACTIVE");
  const [departmentFilter, setDepartmentFilter] = useState("ALL");
  const [locationFilter, setLocationFilter] = useState("ALL");

  // Normalize the authenticated principal (works for admin or user token)
  const currentUserId = useMemo(() => {
    const payload = secureData?.admin || secureData?.auth || null;
    return payload?.user_id ?? payload?.admin_id ?? null;
  }, [secureData]);

  // Refresh triggers from forms
  const handleAddProductComplete = () => {
    setRefreshProducts((x) => !x);
    setShowAddProductForm(false);
  };
  const handleTransferComplete = () => {
    setRefreshProducts((x) => !x);
    setShowTransferForm(false);
  };
  const handlePrintLabelComplete = () => {
    setRefreshProducts((x) => !x);
    setShowPrintLabelForm(false);
  };

  const handlePrintRetailLabelComplete = () => {
    setRefreshProducts((x) => !x);
    setShowPrintRetailLabelForm(false);
  };

  const handleImportComplete = () => {
    setRefreshProducts((x) => !x);
    setShowImportProductForm(false);
  };
    // Open Edit Form
  const handleEditProduct = (inventory) => {
    setSelectedInventory(inventory);
    setShowEditProductForm(true);
  };

  const handleSaveInventory = () => {
    fetchProducts(); // Refresh user list from backend
    setShowEditProductForm(false); // Close the edit form
  };

  // API fetchers
  const fetchProducts = () => {
    fetch("/api/products")
      .then((r) => {
        if (!r.ok) throw new Error("Failed to fetch products");
        return r.json();
      })
      .then(setInventory)
      .catch((e) => console.error("Error fetching products:", e));
  };

  const fetchSecureData = () => {
    fetch("/api/secure-endpoint", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((r) => {
        if (r.status === 401 || r.status === 403) {
          clearSessionAndRedirect();
          throw new Error("Session expired");
        }
        if (!r.ok) throw new Error("Failed to fetch secure data");
        return r.json();
      })
      .then(setSecureData)
      .catch((e) => {
        console.error("Error fetching secure data:", e);
        setError(e.message);
      });
  };

  const fetchLocations = () => {
    fetch("/api/locations")
      .then((r) => {
        if (!r.ok) throw new Error("Failed to fetch locations");
        return r.json();
      })
      .then(setLocations)
      .catch((e) => console.error("Error fetching locations:", e));
  };

  const fetchDepartments = () => {
  fetch("/api/departments")
    .then((r) => {
      if (!r.ok) throw new Error("Failed to fetch departments");
      return r.json();
    })
    .then((data) => setDepartments(Array.isArray(data) ? data : []))
    .catch((e) => console.error("Error fetching departments:", e));
  };

  // Fetch on mount AND when refresh flags change
  useEffect(() => {
    fetchSecureData();
    fetchProducts();
    fetchLocations();
    fetchDepartments();
  }, [refreshTrigger, refreshProducts]);

  const filteredInventory = inventory.filter((p) => {
    const q = searchQuery.toLowerCase().trim();

    const matchesSearch =
      !q ||
      (p.item_first_name?.toLowerCase() || "").includes(q) ||
      (p.item_second_name?.toLowerCase() || "").includes(q) ||
      (p.asset_classification?.toLowerCase() || "").includes(q) ||
      (p.item_code?.toLowerCase() || "").includes(q) ||
      (p.serial_number?.toLowerCase() || "").includes(q) ||
      (p.location_name?.toLowerCase() || "").includes(q) ||
      (p.department_name?.toLowerCase() || "").includes(q);

    const itemIsActive = Number(p.is_active) === 1;

    const matchesStatus =
      statusFilter === "ALL" ||
      (statusFilter === "ACTIVE" && itemIsActive) ||
      (statusFilter === "INACTIVE" && !itemIsActive);

    const matchesDepartment =
      departmentFilter === "ALL" ||
      String(p.department_id || "") === String(departmentFilter);

    const matchesLocation =
      locationFilter === "ALL" ||
      String(p.location_id || "") === String(locationFilter);

    return matchesSearch && matchesStatus && matchesDepartment && matchesLocation;
  });

  const handleExport = async () => {
    try {
      const response = await fetch("/api/export-inventory");

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = "inventory_report.xlsx";

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (e) {
      console.error("Error exporting inventory data:", e);
    }
  };

  return (
    <div className="user-list-container">
      <h2>Product List</h2>

      {error && (
        <div className="error-banner">
          {error}{" "}
          <a href="/login" style={{ marginLeft: 8 }}>Login</a>
        </div>
      )}

      <div className="inventory-filter-card">
        <div className="inventory-filter-header">
          <div>
            <h3>Inventory Filters</h3>
            <p>Search and narrow inventory by status, department and location.</p>
          </div>

          <button
            type="button"
            className="clear-filter-button"
            onClick={() => {
              setSearchQuery("");
              setStatusFilter("ALL");
              setDepartmentFilter("ALL");
              setLocationFilter("ALL");
            }}
          >
            Clear Filters
          </button>
        </div>

        <div className="inventory-filter-grid">
          <div className="inventory-filter-field search-field">
            <label>Search Inventory</label>
            <input
              type="text"
              placeholder="Search name, code, serial, location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="inventory-filter-field">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>

          <div className="inventory-filter-field">
            <label>Department</label>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="ALL">All Departments</option>
              {departments.map((d) => (
                <option key={d.department_id} value={d.department_id}>
                  {d.department_name}
                </option>
              ))}
            </select>
          </div>

          <div className="inventory-filter-field">
            <label>Location</label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="ALL">All Locations</option>
              {locations.map((l) => (
                <option key={l.location_id} value={l.location_id}>
                  {l.location_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="inventory-filter-summary">
          Showing <strong>{filteredInventory.length}</strong> of{" "}
          <strong>{inventory.length}</strong> inventory items
        </div>
      </div>
      <div className="button-container">
        {/* 🟢 Only users with inventory_edit can add or import */}
        {permissions.includes("inventory_add") && (
          <>
            <button onClick={() => setShowAddProductForm(true)}>Add Product</button>
            <button onClick={() => setShowImportProductForm(true)}>Import Products</button>
          </>
        )}

        {/* 🟢 Only users with inventory_edit can transfer */}
        {permissions.includes("inventory_transfer") && (
          <button onClick={() => setShowTransferForm(true)} disabled={!currentUserId}>
            Transfer Inventory
          </button>
        )}

        {/* 🟢 Only users with inventory_printlabel can print */}
        {permissions.includes("inventory_printlabel") && (
          <>
            <button onClick={() => setShowPrintLabelForm(true)} disabled={!currentUserId}>
              Print Label
            </button>
            <button onClick={() => setShowPrintRetailLabelForm(true)} disabled={!currentUserId}>
              Print Retail Label
            </button>
          </>
        )}

        {/* 🟢 Only users with inventory_view can export */}
        {permissions.includes("inventory_view") && (
          <button onClick={handleExport}>Export Inventory</button>
        )}
      </div>


      {/* Add Product */}
      {showAddProductForm && (
        <AddProductForm
            onClose={() => setShowAddProductForm(false)}
            onAddProductComplete={handleAddProductComplete} 
        />
      )}

      {/* Transfer Inventory */}
      {showTransferForm && currentUserId && (
            <InventoryTransferForm
              inventory={inventory}
              locations={locations}
              fetchInventory={fetchProducts}
              userId={currentUserId}
              onClose={() => setShowTransferForm(false)}          
              onTransferComplete={handleTransferComplete}              
            />

      )}

      {/* Print Label */}
      {showPrintLabelForm && currentUserId && (
            <PrintLabelForm
              inventory={inventory}
              fetchInventory={fetchProducts}
              userId={currentUserId}              
              onClose={() => setShowPrintLabelForm(false)}                  
              onPrintLabelComplete={handlePrintLabelComplete}
            />
      )}

      {/* Print Retail Label */}
      {showPrintRetailLabelForm && currentUserId && (
            <PrintRetailLabelForm
              inventory={inventory}
              fetchInventory={fetchProducts}
              userId={currentUserId}              
              onClose={() => setShowPrintRetailLabelForm(false)}                  
              onPrintRetailLabelComplete={handlePrintRetailLabelComplete}
            />
      )}      

      {/* Import Products */}
      {showImportProductForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-button" onClick={() => setShowImportProductForm(false)}>&times;</button>
            <ImportProductForm onTransferComplete={handleImportComplete} />
          </div>
        </div>
      )}

      <div className="UserList-container">
        <table>
          <thead>
            <tr>
              <th>First Name</th>
              <th>Second Name</th>
              <th>Asset Classification</th>
              <th>Department</th>
              <th>Product Code</th>
              <th>Serial Number</th>
              <th>Location</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredInventory.length > 0 ? (
              filteredInventory.map((p) => (
                <tr key={p.inventory_id ?? p.item_code}>
                  <td>{p.item_first_name}</td>
                  <td>{p.item_second_name}</td>
                  <td>{p.asset_classification}</td>
                  <td>{p.department_name}</td>
                  <td>{p.item_code}</td>
                  <td>{p.serial_number}</td>
                  <td>{p.location_name}</td>
                  <td>
                    <span
                      className={
                        Number(p.is_active) === 1
                          ? "inventory-status-badge active"
                          : "inventory-status-badge inactive"
                      }
                    >
                      {Number(p.is_active) === 1 ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button onClick={() => handleEditProduct(p)}>Edit</button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9">No inventory items match your search.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {showEditProductForm && selectedInventory && (
        <EditProductForm
          inventory={selectedInventory}
          onClose={() => setShowEditProductForm(false)}
          onSave={handleSaveInventory}
        />
      )}
    </div>
  );
};

export default InventorySearch;
